/**
 * Encode string to Base62 format
 *
 * Converts UTF-8 string to hex, then to BigInt, then to Base62
 * This matches the algorithm in SSO_API_README.md sample code
 *
 * Base62 uses characters: 0-9, A-Z, a-z
 * Example: "202510171122" → "KCRUyKGJ0bbEPEcE"
 *
 * @param input - String to encode
 * @returns Base62 encoded string
 */
export const encodeBase62 = (input: string): string => {
  const BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  if (input.length === 0) {
    return '';
  }

  // Convert string to hex
  const encoder = new TextEncoder();
  const bytes = encoder.encode(input);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }

  // Convert hex to BigInt
  let value = BigInt(`0x${hex}`);
  let result = '';

  // Convert BigInt to Base62
  const base = BigInt(62);
  const zero = BigInt(0);

  while (value > zero) {
    const divmod = value % base;
    result = BASE62[Number(divmod)] + result;
    value /= base;
  }

  // Handle leading zeros
  for (let i = 0; i < input.length; i++) {
    if (input.charCodeAt(i) === 0) {
      result = BASE62[0] + result;
    } else {
      break;
    }
  }

  return result || '0';
};

/**
 * Generate security_id in Base62 format from BUILDSELF@YYYYMMDDHHMM
 *
 * As per specification in SSO_TEST_README.md:
 * "security_idはタイムスタンプベースで生成され、時間ベースの検証が可能"
 * Format: "BUILDSELF@YYYYMMDDHHMM"
 *
 * Example:
 * - Date/time: 2025-10-17 11:22
 * - String before encoding: "BUILDSELF@202510171122"
 * - Base62: encoded result
 *
 * @returns Security ID in Base62 format
 */
export const generateSecurityId = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  const minute = String(now.getMinutes()).padStart(2, '0');

  // Format: BUILDSELF@YYYYMMDDHHMM
  const timestampString = `BUILDSELF@${year}${month}${day}${hour}${minute}`;

  return encodeBase62(timestampString);
};
